import React from "react";
import { FormattedMessage } from "react-intl";
import { TransitionGroup } from "react-transition-group";

import { InstitutionModel } from "@courses-models/institution";
import { UnicUserModel, UserTypes } from "@courses-models/user";
import {
  redirectToLoginConnector,
  signOutFromLoginConnector,
} from "@courses-utils/gateway-auth";

import { asyncTimeout } from "@use/async-timeout";
import { hrtime } from "@use/universal-hrtime";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { colors, loginConnector, urls } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { config } from "@apps/viewer/src/ts/components/layout/Header/config";
import { UserInstitutionFade } from "@apps/viewer/src/ts/components/layout/Header/transitions/UserInstitutionFade";
import { Chevron } from "@apps/viewer/src/ts/components/utils/Chevron/Chevron";
import { Dropdown } from "@apps/viewer/src/ts/components/utils/Dropdown/Dropdown";

/**
 * Specification of props required to render <UserInfo />.
 */
type UserInfoProps = {
  /**
   * A reference to the application lifecycle store, so we can reference data
   * about the currently signed in user.
   */
  store: Store;
};

/**
 * Specification of lifecycle state of <UserInfo />.
 */
type UserInfoState = {
  /**
   * Will contain the fetched name of the user institution, so that it can be
   * injected into the user profile dropdown.
   */
  institutionName?: string;

  /**
   * Will contain the id of the institution, which we fetched the name from.
   */
  institutionId?: string;

  /**
   * Specifies if the profile dropdown should currently be shown.
   */
  showDropdown?: boolean;
};

/**
 * The search bar will be placed next to the logo, allowing users to easily
 * search for courses matching the specified search words.
 */
export class UserInfoUnwrapped extends React.PureComponent<
  UserInfoProps,
  UserInfoState
> {
  /**
   * Set up initial state, so this.state can be safely accessed.
   */
  public readonly state: UserInfoState = {};

  /**
   * Will contain a reference to the user name wrapper, so that we can make
   * the dropdown point towards it properly.
   */
  protected readonly wrapperRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * Derive institution state based on props, so that we can easily fetch the
   * current institution name in component lifecycle hooks.
   */
  // tslint:disable-next-line function-name
  public static getDerivedStateFromProps(
    nextProps: UserInfoProps,
    prevState: UserInfoState
  ): Partial<UserInfoState> | null {
    // If the user is signed in as an e-key user, then we cannot display any
    // kind of institution association
    if (!(nextProps.store.user instanceof UnicUserModel)) {
      return {
        institutionId: undefined,
        institutionName: undefined,
      };
    }

    // If user institution association has been changed, then reset state so
    // we can fetch data in the component lifecycle hooks
    if (prevState.institutionId !== nextProps.store.user.institutionId) {
      return {
        institutionId: nextProps.store.user.institutionId,
        institutionName: undefined,
      };
    }

    // If we get here, nothing has changed
    return null; // tslint:disable-line no-null-keyword
  }

  /**
   * Fetch new institution name, if the user institution association has been
   * changed during lifecycle.
   */
  public componentDidUpdate(): void {
    if (
      this.state.showDropdown &&
      this.state.institutionId &&
      this.state.institutionName === undefined
    ) {
      this.fetchInstitutionData(this.state.institutionId).catch((err) => {
        throw err;
      });
    }
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element | undefined {
    // Bail out if no user was available
    if (!this.props.store.user) {
      return undefined;
    }

    return (
      <div ref={this.wrapperRef} onClick={this.onUsernameClick} role="button">
        <i className="icon">{getIcon("user")}</i>
        <span className="chevron">
          <Chevron
            vertical
            size={10}
            inverseDuration={200}
            inversed={this.state.showDropdown}
          />
        </span>

        {this.renderDropdown()}

        <style jsx>{`
          div {
            /** Indicate that the element is clickable */
            cursor: pointer;

            /** Ensure UserInfo always takes up the space it requires */
            flex-shrink: 0;

            /** Apply default color */
            & .icon > :global(.svgIcon) > :global(svg) > :global(path) {
              fill: ${config.toolColor};
            }

            & .chevron {
              border-color: ${config.toolColor};
            }

            /** Apply hover colors */
            &:hover .icon > :global(.svgIcon) > :global(svg) > :global(path) {
              fill: ${colors.orange};
            }

            &:hover .chevron {
              border-color: ${colors.orange};
            }
          }

          .icon {
            /** Set up sizing of the icon */
            display: inline-block;
            height: 30px;
            width: 32px;
          }

          .chevron {
            /**
                         * Ensure that the wrapper is position to the right
                         * of the button and that the chevron will be contained
                         * within
                         */
            position: relative;
            display: inline-block;

            /** Render chevron properly */
            width: 14px;
            height: 30px;
            border-color: #8f8f8f;

            /** Setup space between chevron and text */
            margin-left: 10px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renders the dropdown that'll become visible when the user clicks his own
   * profile name.
   */
  protected renderDropdown(): React.JSX.Element {
    // We're going to want the user to sign in again immediately after
    // having signed out, so generate signin url
    const signInUrl = redirectToLoginConnector(
      loginConnector.profile,
      {
        ...loginConnector.signIn,
        failureUrl: urls.mfOverview,
        successUrl: urls.mfOverview,
      },
      true
    );

    // Now generate the url to sign the user out through the LoginConnector,
    // and then prompting him to sign in again immediately after
    const signOutUrl = signOutFromLoginConnector(
      loginConnector.profile,
      {
        ...loginConnector.signOut,
        successUrl: signInUrl,
      },
      true
    );

    return (
      <Dropdown
        active={this.state.showDropdown}
        pointAt={this.wrapperRef}
        onClose={this.onDropdownClose}
      >
        <div className="wrapper">
          {this.renderUserData()}

          <a href={signOutUrl} role="button">
            <i>{getIcon("signIn")}</i>

            <FormattedMessage
              id="Header__userDropdown__signOut"
              defaultMessage="Log ind med en anden profil"
            />
          </a>
        </div>

        <style jsx>{`
          .wrapper {
            /** Apply internal padding */
            padding: 20px 0;

            /** By default element isn't clickable */
            cursor: default;
          }

          a {
            /** Center items within vertically */
            display: flex;
            align-items: center;

            /**
                         * Set up basic padding around the element (the 6px top
                         * padding is applied to take into account that the icon
                         * will push text further down, and we want the white-
                         * space to visually align with the text instead of the
                         * icon).
                         */
            padding: 10px 20px;
            margin: -10px 0;

            /** Prevent line-breaks within the label */
            white-space: nowrap;

            /** Set up text styling */
            color: ${colors.black};
            line-height: 1em;
            text-decoration: none;

            /** Prepare hover transition */
            transition: background-color 0.1s linear, color 0.1s linear;

            & i {
              /** Set up icon sizing */
              display: inline-block;
              width: 16px;
              height: 20px;

              /** Apply margin between icon and text */
              margin-right: 10px;

              & > :global(.svgIcon) > :global(svg) > :global(path) {
                /** Apply default font color */
                fill: ${colors.black};
              }
            }

            /** Apply highlight color on hover */
            &:hover {
              background-color: ${config.hoverBackgroundColor};
              color: ${colors.orange};

              & i > :global(.svgIcon) > :global(svg) > :global(path) {
                fill: ${colors.orange};
              }
            }
          }
        `}</style>
      </Dropdown>
    );
  }

  /**
   * Renderer that'll inject data about the currently signed in user, so that
   * it's available in the dropdown.
   */
  protected renderUserData(): React.JSX.Element {
    const { user } = this.props.store;
    const { institutionId } = this.state;

    return (
      <div className="user">
        <b>{user.name}</b>
        <br />

        {institutionId !== undefined
          ? this.renderUserInstitution()
          : this.renderUserType()}

        <style jsx>{`
          .user {
            /** Apply required margins */
            padding: 0 20px calc(20px - 0.15em);
            margin: -0.15em 0 20px;

            /** Add separator */
            border-bottom: 1px solid #ccc;

            /** Adjust line-height */
            line-height: 1.3em;

            /** Disable text-selection */
            user-select: none;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the name of the user's institution in the user
   * data segment.
   */
  protected renderUserInstitution(): React.JSX.Element {
    const { institutionName } = this.state;

    return (
      <div>
        <TransitionGroup component={React.Fragment}>
          {institutionName !== undefined ? (
            <UserInstitutionFade key="institution">
              {(ref) => <span ref={ref}>{institutionName}</span>}
            </UserInstitutionFade>
          ) : (
            <UserInstitutionFade key="throbber">
              {(ref) => (
                <span ref={ref} className="throbber">
                  <FormattedMessage
                    id="Header__userDropdown__loadingInstitution"
                    defaultMessage="Indlæser"
                  />
                  <i>.</i>
                  <i>.</i>
                  <i>.</i>
                </span>
              )}
            </UserInstitutionFade>
          )}
        </TransitionGroup>

        <style jsx>{`
          div {
            /** Allow exit-transition to use position: absolute */
            position: relative;
          }

          .throbber {
            & i {
              /** Prepare fade in-out animation */
              animation: fadeDot 1.2s linear backwards infinite;

              /** Defer dot 2 and 3 */
              &:nth-child(3) {
                animation-delay: 0.2s;
              }

              &:nth-child(4) {
                animation-delay: 0.4s;
              }
            }

            /** Fade bullets in / out of view */
            @keyframes fadeDot {
              0% {
                opacity: 0;
              }

              16.66% {
                opacity: 1;
              }

              50% {
                opacity: 1;
              }

              66.66% {
                opacity: 0;
              }

              100% {
                opacity: 0;
              }
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the user's type into the DOM (this is used, if we
   * were unable to identify the user's institution assoication).
   */
  protected renderUserType(): React.JSX.Element {
    // We automatically assume that e-key users are teachers
    if (!(this.props.store.user instanceof UnicUserModel)) {
      return (
        <FormattedMessage
          id="Header__userDropdown__teacher"
          defaultMessage="Lærer"
        />
      );
    }

    // ... Otherwise return label based on user type
    switch (this.props.store.user.userType) {
      case UserTypes.STUDENT:
        return (
          <FormattedMessage
            id="Header__userDropdown__student"
            defaultMessage="Elev"
          />
        );

      default:
        return (
          <FormattedMessage
            id="Header__userDropdown__teacher"
            defaultMessage="Lærer"
          />
        );
    }
  }

  /**
   * Listener that's triggered when the user clicks the user info component,
   * and prompts opening of the dropdown.
   */
  protected onUsernameClick = () => {
    this.setState({ showDropdown: true });
  };

  /**
   * Listener that's triggered when the user somehow prompts for the user
   * dropdown to be closed.
   */
  protected onDropdownClose = () => {
    this.setState({ showDropdown: false });
  };

  /**
   * Helper that fetches data about the institution with the given id, and
   * applies it to the component so institution name can be displayed in the
   * dropdown.
   */
  protected async fetchInstitutionData(institutionId: string): Promise<void> {
    const startedAt = hrtime();

    // Fetch the data
    const institution = await InstitutionModel.GET_BY_ID(institutionId);

    // Wait for the desired minimum duration before applying data
    await asyncTimeout(Math.max(0, 650 - (hrtime() - startedAt)));

    // Apply institution name, if user institution association hasn't been
    // changed yet
    if (this.state.institutionId === institutionId) {
      this.setState({ institutionName: institution.name });
    }
  }
}

// tslint:disable-next-line variable-name
export const UserInfo = injectStore(UserInfoUnwrapped);
